import { parseDuration } from './duration.js'
import { isBlocked, setBlockedUntil } from './storage.js'
import { fireEvent } from './events.js'
import { setAttribution } from './attribution.js'
import { watchFormsForSuccess } from './form-watcher.js'

export class PopupController {
  constructor(element) {
    this.element = element
    this.popupId = element.getAttribute('data-popup')
    if (!this.popupId) {
      throw new Error('PopupController: data-popup attribute is required')
    }
    const delayRaw = element.getAttribute('data-popup-delay')
    if (!delayRaw) {
      throw new Error('PopupController: data-popup-delay attribute is required')
    }
    const frequencyRaw = element.getAttribute('data-popup-frequency')
    if (!frequencyRaw) {
      throw new Error('PopupController: data-popup-frequency attribute is required')
    }
    this.delayMs = parseDuration(delayRaw)
    this.frequencyMs = parseDuration(frequencyRaw)
    this.convertedFrequencyMs = parseDuration(
      element.getAttribute('data-popup-converted-frequency') ?? 'never'
    )
    this.attributionTtlMs = parseDuration(
      element.getAttribute('data-popup-attribution-ttl') ?? '30m'
    )
    // Optional: CSS selector for a specific form on the destination page.
    // If set, only success of that form counts as a popup conversion.
    this.convertTarget = element.getAttribute('data-popup-convert-target') || null

    this._timerId = null
    this._isShown = false
    this._cleanupFns = []
  }

  start() {
    if (isBlocked(this.popupId)) return
    this._timerId = setTimeout(() => this._show(), this.delayMs)
  }

  _show() {
    // `data-popup-display` lets the designer choose flex / block / grid / etc.
    // Default is "flex" — most popups center content via flexbox.
    const displayMode = this.element.getAttribute('data-popup-display') || 'flex'
    this.element.style.display = displayMode
    // Force reflow so the .popup-visible transition triggers next frame
    void this.element.offsetWidth
    this.element.classList.add('popup-visible')
    this._isShown = true
    setBlockedUntil(this.popupId, Date.now() + this.frequencyMs)
    fireEvent('popup_shown', {
      popup_id: this.popupId,
      popup_delay: this.delayMs / 1_000
    })
    this._bindCloseHandlers()
    this._bindCtaHandlers()
    this._watchInPopupForm()
  }

  _bindCloseHandlers() {
    // Close buttons — fire on any click within the marked element.
    // stopPropagation prevents page-level click listeners (cookie banners,
    // Webflow interactions, GTM auto-track) from reacting to popup-internal
    // clicks. Tracking events for the popup are still fired via dataLayer.
    const closeBtns = this.element.querySelectorAll('[data-popup-close]')
    closeBtns.forEach(btn => {
      const handler = (e) => {
        e.preventDefault()
        e.stopPropagation()
        this._dismiss('close-button')
      }
      btn.addEventListener('click', handler)
      this._cleanupFns.push(() => btn.removeEventListener('click', handler))
    })

    // Overlay backdrops — fire only when click target is the overlay itself,
    // not when click bubbles up from a descendant (e.g. popup card content).
    const overlays = this.element.querySelectorAll('[data-popup-overlay]')
    overlays.forEach(overlay => {
      const handler = (e) => {
        if (e.target === overlay) {
          e.stopPropagation()
          this._dismiss('overlay')
        }
      }
      overlay.addEventListener('click', handler)
      this._cleanupFns.push(() => overlay.removeEventListener('click', handler))
    })

    const onKey = (e) => {
      if (e.key === 'Escape') this._dismiss('esc')
    }
    document.addEventListener('keydown', onKey)
    this._cleanupFns.push(() => document.removeEventListener('keydown', onKey))

    const onDocClick = (e) => {
      if (!this.element.contains(e.target)) this._dismiss('overlay')
    }
    setTimeout(() => document.addEventListener('click', onDocClick), 0)
    this._cleanupFns.push(() => document.removeEventListener('click', onDocClick))
  }

  _bindCtaHandlers() {
    const ctas = this.element.querySelectorAll('[data-popup-convert]')
    ctas.forEach(cta => {
      const handler = () => this._onCtaClick()
      cta.addEventListener('click', handler)
      this._cleanupFns.push(() => cta.removeEventListener('click', handler))
    })
  }

  _onCtaClick() {
    fireEvent('popup_clicked', { popup_id: this.popupId })
    setAttribution(this.popupId, this.attributionTtlMs, this.convertTarget)
    const cooldown = this.convertedFrequencyMs === Infinity
      ? Infinity
      : Date.now() + this.convertedFrequencyMs
    setBlockedUntil(this.popupId, cooldown)
  }

  _watchInPopupForm() {
    const stop = watchFormsForSuccess(this.element, () => {
      fireEvent('popup_converted', { popup_id: this.popupId })
      setBlockedUntil(this.popupId,
        this.convertedFrequencyMs === Infinity
          ? Infinity
          : Date.now() + this.convertedFrequencyMs
      )
    })
    this._cleanupFns.push(stop)
  }

  _dismiss(method) {
    if (!this._isShown) return
    this._isShown = false
    this.element.classList.remove('popup-visible')

    // Wait for CSS transition to finish before hiding the element.
    // Reads the actual transition-duration so the script matches whatever
    // duration the designer set in their CSS / Webflow Style panel.
    const durationMs =
      parseFloat(getComputedStyle(this.element).transitionDuration) * 1000 || 0
    if (durationMs > 0) {
      setTimeout(() => {
        // Guard: don't hide if a new _show() ran in the meantime
        if (!this._isShown) this.element.style.display = 'none'
      }, durationMs + 50)
    } else {
      this.element.style.display = 'none'
    }

    fireEvent('popup_dismissed', {
      popup_id: this.popupId,
      dismiss_method: method
    })
    this._cleanup()
  }

  _cleanup() {
    this._cleanupFns.forEach(fn => fn())
    this._cleanupFns = []
  }
}
